import { useEffect, useId, useMemo, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { DevTool } from '@hookform/devtools'
import {
  PrimaryButton, Icon, Spacer, NakedButton, Toasts, Toast,
} from '@cb/apricot-react'
import { CBInputMethod } from '@cb/apricot'
import { showNumbersOnly } from '@myap/ui-library'
import { BfsRecoveryEmail as RecoveryEmail, BfsPhoneNumber as PhoneNumber } from './fields'
import {
  DEFAULT_BIG_FUTURE_VALUES,
  FIELD_CONFIRM_PHONE_NUMBER,
  FIELD_PHONE_NUMBER,
  FIELD_RECOVERY_EMAIL,
} from './constants'
import styles from './profileandregform.module.scss'
import useStudentBfsMutation from '../hooks/useStudentBfsMutation'
import useUserSettingsQuery from '../../../hooks/useUserSettingsQuery'
import useStudentJoinCourseDetailsQuery from '../hooks/useStudentJoinCourseDetailsQuery'
import useStudentInfoAndRegistrationQuery from '../hooks/useStudentInfoAndRegistrationQuery'
import useStudentSectionDetailsQuery from '../hooks/useStudentSectionDetailsQuery'
import { valueOrNull } from '../../../utilities/utils'

function BigFutureForm({ changeStep, nextStep, withHeader = true, adjustHeight }) {
  const modalHeaderId = useId()
  const isStandAlone = typeof changeStep !== 'function'
  let toastTimerId = null
  const isSubmittingRef = useRef(false)
  const [ showToast, setShowToast ] = useState(false)

  const { educationPeriod: { code: currentEPC, hasEnrollments }} = useUserSettingsQuery()

  // Find if course is bfs eligible
  const { data: joinCourseData } = useStudentJoinCourseDetailsQuery({
    otherSkipConditions: isStandAlone,
  })
  const { data: sectionDetailsData } = useStudentSectionDetailsQuery({
    enrollmentCode: joinCourseData?.enrollmentCode,
    addBfsEligibility: !isStandAlone,
    otherSkipConditions: isStandAlone,
  })

  const { mutate, loading: mutationLoading } = useStudentBfsMutation({
    educationPeriod: currentEPC,
  })

  const {
    data: registrationData,
  } = useStudentInfoAndRegistrationQuery({
    educationPeriod: currentEPC,
    refresh: !hasEnrollments && !isStandAlone,
  })

  const recoveryEmailAddress = registrationData?.recoveryEmailAddress
  const bfsPhoneNumber = registrationData?.bfsPhoneNumber

  const lockForm = Boolean(
    recoveryEmailAddress || bfsPhoneNumber
  )

  const formValues = useMemo(() => ({
    ...DEFAULT_BIG_FUTURE_VALUES,
    ...(recoveryEmailAddress
      ? { [FIELD_RECOVERY_EMAIL]: recoveryEmailAddress }
      : {}),
    ...(bfsPhoneNumber
      ? {
        [FIELD_PHONE_NUMBER]: bfsPhoneNumber,
        [FIELD_CONFIRM_PHONE_NUMBER]: bfsPhoneNumber,
      }
      : {}),
  }), [ recoveryEmailAddress, bfsPhoneNumber ])

  const methods = useForm({
    mode: 'onChange',
    disabled: isSubmittingRef.current || lockForm,
    values: formValues,
  })

  const {
    handleSubmit,
    control,
    formState: { isValid, isDirty },
  } = methods

  useEffect(() => {
    CBInputMethod()
  }, [])

  useEffect(() => {
    if (adjustHeight) {
      adjustHeight()
    }
  }, [adjustHeight])

  const onSubmit = async values => {
    if (isSubmittingRef.current) {
      return
    }

    isSubmittingRef.current = true
    try {
      await mutate({
        variables: {
          code: currentEPC,
          input: {
            sectionOrgId: sectionDetailsData?.orgId,
            phoneNumber: showNumbersOnly(values[FIELD_PHONE_NUMBER]),
            recoveryEmailAddress: valueOrNull(values[FIELD_RECOVERY_EMAIL]),
            hasNoEnrollments: !hasEnrollments,
          },
          isStudent: true,
          isBfs: true,
        },
      })

      isSubmittingRef.current = false

      if (!isStandAlone) {
        changeStep(nextStep)
      } else {
        setShowToast(true)
        toastTimerId = setTimeout(() => setShowToast(false), 5000)
      }
    } catch (err) {
      isSubmittingRef.current = false
      console.error('Error submitting BigFuture form:', err)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {withHeader ? (
          <div className="cb-modal-header cb-modal-has-close">
            <h2 className="cb-modal-title" id={`apricot_modal_header_${modalHeaderId}`}>
              {isStandAlone ? 'BigFuture' : 'AP Registration - Step 3'}
            </h2>
            <button
              type="button"
              className="cb-btn cb-btn-square cb-btn-greyscale cb-btn-close"
              aria-describedby={`apricot_modal_header_${modalHeaderId}`}
              data-cb-modal-close="true"
            >
              <span />
              <Icon name="x-mark" />
              <span className="sr-only">Close Modal</span>
            </button>
          </div>
        ) : null}
        <div id="modalContent" className="cb-modal-content">
          <div className={styles.registration}>
            <Toasts bottom>
              <Toast
                timeout={5000}
                type="success"
                message="Engage With BFS saved"
                controlled={true}
                show={showToast}
                onDismiss={() => {
                  clearTimeout(toastTimerId)
                  setShowToast(false)
                }}
              />
            </Toasts>
            <strong>Engage with BFS</strong>
            <p>
              Enter your number to get BigFuture School, a free app that provides you with college
              and career resources. After you test, we&#39;ll text you a link to download the app
              and send you a code you&#39;ll use to sign in.
              <strong> We won&#39;t use your phone number for any other reason.</strong>
            </p>
            <Spacer size="24" />
            <PhoneNumber />
            <Spacer size="24" />
            <RecoveryEmail />
          </div>
        </div>
        <div className="cb-modal-footer">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginRight: 'auto' }}>
              <p className="cb-margin-bottom-16">
                Before clicking <strong> Save </strong>, confirm that all information entered on
                this form is accurate.
              </p>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              {
                !isStandAlone && (
                  <NakedButton
                    type="button"
                    onClick={() => changeStep(nextStep)}
                  >
                    Skip this step
                  </NakedButton>
                )
              }
              <PrimaryButton
                type="submit"
                disabled={
                  !isValid || !isDirty || isSubmittingRef.current || mutationLoading || lockForm
                }
                loading={isSubmittingRef.current || mutationLoading}
              >
                Save
              </PrimaryButton>
            </div>
          </div>
        </div>
        {/* eslint-disable-next-line no-undef, no-underscore-dangle */}
        {globalThis?.__DEV__ ? <DevTool control={control} /> : null}
      </form>
    </FormProvider>
  )
}

export default BigFutureForm
